import type { GroupPublic } from '@repo/types/groups';
import type { UserPublic } from '@repo/types/users';

import { useGetAssignedGroupsInfinite } from '@repo/api/group/useGetAssignedGroupsInfinite';
import { useGetUsersInfinite } from '@repo/api/user/useGetUsersInfinite';
import { useDebounceValue } from '@repo/hooks/useDebounceValue';
import { useInfiniteQueryResult } from '@repo/hooks/useInfiniteQueryResult';
import { getUserLvl } from '@repo/lib/user-lvl/getUserLvl';
import { UserIcon, UsersIcon } from 'lucide-react';
import { useEffect, useState } from 'react';

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { InfiniteScroll } from '@/components/ui/infinite-scroll';
import { SearchInput } from '@/components/ui/input';
import {
  SelectList,
  SelectListElement,
  SelectListElementContent,
  SelectListElementDescription,
  SelectListElementSkeleton,
  SelectListElementTitle,
  SelectListElementTrigger,
  SelectListInfiniteQueryStates,
} from '@/components/ui/select-list';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Tag } from '@/components/ui/tags';
import { getAvatar } from '@/utils/getAvatar';

export interface QuizBuilderMembersDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  users: UserPublic[];
  groups: GroupPublic[];
  onUsersChange: (users: UserPublic[]) => void;
  onGroupsChange: (groups: GroupPublic[]) => void;
}

export default function QuizBuilderMembersDialog(quizBuilderMembersDialogProps: QuizBuilderMembersDialogProps) {
  const { open, onOpenChange, onUsersChange, onGroupsChange, users, groups } = quizBuilderMembersDialogProps;

  const [searchValue, setSearchValue] = useState<string>('');
  const searchDebouncedValue: string = useDebounceValue(searchValue, 400);

  const [selectedUsers, setSelectedUsers] = useState<UserPublic[]>([]);
  const [selectedGroups, setSelectedGroups] = useState<GroupPublic[]>([]);

  const { errorMessage: usersErrorMessage, ...getUsersInfinite } = useGetUsersInfinite(searchDebouncedValue);

  const { data: usersData, isEmpty: usersIsEmpty, noResults: usersNoResults } = useInfiniteQueryResult(getUsersInfinite.data, searchDebouncedValue);

  const { errorMessage: groupsErrorMessage, ...getAssignedGroupsInfinite } = useGetAssignedGroupsInfinite(searchDebouncedValue);

  const { data: groupsData, isEmpty: groupsIsEmpty, noResults: groupsNoResults } = useInfiniteQueryResult(getAssignedGroupsInfinite.data, searchDebouncedValue);

  const onRemoveUser = (id: number) => {
    const updatedUsers = selectedUsers.filter((u) => u.id !== id);
    setSelectedUsers(updatedUsers);
    onUsersChange(updatedUsers);
  };

  const onRemoveGroup = (id: number) => {
    const updatedGroups = selectedGroups.filter((g) => g.id !== id);
    setSelectedGroups(updatedGroups);
    onGroupsChange(updatedGroups);
  };

  const onClose = () => {
    onOpenChange(false);
    setSearchValue('');
  };

  // Sync selected users and groups with props only when dialog opens
  useEffect(() => {
    if (open) {
      setSelectedUsers(users);
      setSelectedGroups(groups);
    }
  }, [open, users, groups]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={'sm:h-[85dvh] sm:min-h-96 sm:max-w-108'}>
        <DialogBody>
          <DialogHeader className={'border-border-primary bg-card border-b'}>
            <DialogTitle>Dodaj uczestników</DialogTitle>
          </DialogHeader>
          <DialogDescription className={'sr-only'}>Formularz do dodawania uczestników</DialogDescription>
          <Tabs className={'flex grow flex-col gap-3 overflow-y-auto px-3 py-3 md:px-4'} defaultValue={'groups'}>
            <div className={'flex flex-col gap-4'}>
              <TabsList className={'grid w-full grid-cols-2'}>
                <TabsTrigger value={'groups'}>Grupy</TabsTrigger>
                <TabsTrigger value={'users'}>Użytkownicy</TabsTrigger>
              </TabsList>
              <SearchInput setValue={setSearchValue} placeholder="Wyszukaj..." />
              <div className="flex flex-wrap gap-1">
                {selectedUsers.map(({ id, name, surname, avatarURL }) => (
                  <Tag key={id} onRemove={() => onRemoveUser(id)} variant={'muted'} border>
                    <Avatar size={'xs'}>
                      <AvatarImage src={getAvatar(avatarURL)} alt={name + '_' + surname} />
                      <AvatarFallback>
                        <UserIcon />
                      </AvatarFallback>
                    </Avatar>
                    {name} {surname.at(0)}.
                  </Tag>
                ))}
                {selectedGroups.map(({ id, name, avatarURL }) => (
                  <Tag key={id} onRemove={() => onRemoveGroup(id)} variant={'muted'} border>
                    <Avatar size={'xs'}>
                      <AvatarImage src={getAvatar(avatarURL)} alt={name} />
                      <AvatarFallback>
                        <UsersIcon />
                      </AvatarFallback>
                    </Avatar>
                    {name}
                  </Tag>
                ))}
              </div>
            </div>
            <TabsContent value={'users'}>
              <SelectList
                type={'multiple'}
                selected={selectedUsers.map((u) => String(u.id))}
                setSelected={(ids) => {
                  const selectedIds = new Set(ids.map(Number));
                  const updatedUsers = [...selectedUsers.filter((u) => selectedIds.has(u.id)), ...usersData.filter((u) => selectedIds.has(u.id) && !selectedUsers.some((s) => s.id === u.id))];
                  setSelectedUsers(updatedUsers);
                  onUsersChange(updatedUsers);
                }}
              >
                <SelectListInfiniteQueryStates
                  query={getUsersInfinite}
                  isEmpty={usersIsEmpty}
                  noResults={usersNoResults}
                  isEmptyMessage={'Wyszukaj użytkownika'}
                  noResultMessage={'Nie znaleziono użytkownika'}
                  errorMessage={usersErrorMessage}
                />

                {usersData.map((user) => (
                  <SelectListElement key={user.id} id={String(user.id)}>
                    <Avatar size={'sm'}>
                      <AvatarImage src={getAvatar(user.avatarURL)} alt={user.name} />
                      <AvatarFallback>{user.name[0]}</AvatarFallback>
                    </Avatar>
                    <SelectListElementContent>
                      <SelectListElementTitle>
                        {user.name} {user.surname}
                      </SelectListElementTitle>
                      <SelectListElementDescription>{getUserLvl(user.lvl)}</SelectListElementDescription>
                    </SelectListElementContent>

                    <SelectListElementTrigger>
                      {({ selected, toggleElement }) => (
                        <Checkbox
                          checked={selected}
                          onCheckedChange={() => {
                            toggleElement();
                          }}
                          className="after:absolute after:inset-0"
                        />
                      )}
                    </SelectListElementTrigger>
                  </SelectListElement>
                ))}
                <InfiniteScroll
                  hasNextPage={getUsersInfinite.hasNextPage}
                  isLoading={getUsersInfinite.isLoading}
                  isFetching={getUsersInfinite.isFetching}
                  next={getUsersInfinite.fetchNextPage}
                  threshold={0.65}
                >
                  {getUsersInfinite.hasNextPage && <SelectListElementSkeleton />}
                </InfiniteScroll>
              </SelectList>
            </TabsContent>
            <TabsContent value={'groups'}>
              <SelectList
                type={'multiple'}
                selected={selectedGroups.map((g) => String(g.id))}
                setSelected={(ids) => {
                  const selectedIds = new Set(ids.map(Number));
                  const updatedGroups = [...selectedGroups.filter((g) => selectedIds.has(g.id)), ...groupsData.filter((g) => selectedIds.has(g.id) && !selectedGroups.some((s) => s.id === g.id))];
                  setSelectedGroups(updatedGroups);
                  onGroupsChange(updatedGroups);
                }}
              >
                <SelectListInfiniteQueryStates
                  query={getAssignedGroupsInfinite}
                  isEmpty={groupsIsEmpty}
                  noResults={groupsNoResults}
                  isEmptyMessage={'Wyszukaj grupy'}
                  noResultMessage={'Nie znaleziono grupy'}
                  errorMessage={groupsErrorMessage}
                />

                {groupsData.map((group) => (
                  <SelectListElement key={group.id} id={String(group.id)}>
                    <Avatar size={'sm'}>
                      <AvatarImage src={getAvatar(group.avatarURL)} alt={group.name} />
                      <AvatarFallback>{group.name[0]}</AvatarFallback>
                    </Avatar>
                    <SelectListElementContent>
                      <SelectListElementTitle>{group.name}</SelectListElementTitle>
                      <SelectListElementDescription>{group.users.length} użytkowników</SelectListElementDescription>
                    </SelectListElementContent>

                    <SelectListElementTrigger>
                      {({ selected, toggleElement }) => (
                        <Checkbox
                          checked={selected}
                          onCheckedChange={() => {
                            toggleElement();
                          }}
                          className="after:absolute after:inset-0"
                        />
                      )}
                    </SelectListElementTrigger>
                  </SelectListElement>
                ))}
                <InfiniteScroll
                  hasNextPage={getAssignedGroupsInfinite.hasNextPage}
                  isLoading={getAssignedGroupsInfinite.isLoading}
                  isFetching={getAssignedGroupsInfinite.isFetching}
                  next={getAssignedGroupsInfinite.fetchNextPage}
                  threshold={0.65}
                >
                  {getAssignedGroupsInfinite.hasNextPage && <SelectListElementSkeleton />}
                </InfiniteScroll>
              </SelectList>
            </TabsContent>
          </Tabs>
        </DialogBody>
        <DialogFooter className={'border-border-primary bg-accent dark:bg-card border-t'}>
          <Button type={'button'} variant={'outline'} onClick={onClose} className={'w-full md:w-fit'}>
            Zamknij
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
